import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import PaxCalculator from "@/components/PaxCalculator";
import PostcodeChecker from "@/components/PostcodeChecker";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight, PackageCheck, Calculator, CalendarCheck, Wrench,
  ShieldCheck, BadgeCheck, Hammer, MapPinned, Boxes, ChefHat, Sofa,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1608613304899-ea8098577e38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxjYXJwZW50ZXIlMjBhc3NlbWJsaW5nJTIwZnVybml0dXJlJTIwdG9vbHN8ZW58MHx8fHwxNzg5NDk3MDU2fDA&ixlib=rb-4.1.0&q=85",
  pax: "https://images.unsplash.com/photo-1781249144138-0c63b4d5876e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB3YXJkcm9iZSUyMGludGVyaW9yJTIwc2NhbmRpbmF2aWFufGVufDB8fHx8MTc4OTQ5NzA1Nnww&ixlib=rb-4.1.0&q=85",
  kitchen: "https://images.unsplash.com/photo-1585128833500-ec98262cb4f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxzY2FuZGluYXZpYW4lMjBraXRjaGVuJTIwaW50ZXJpb3IlMjB3b29kJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3ODk0OTcwNTZ8MA&ixlib=rb-4.1.0&q=85",
  tools: "https://images.unsplash.com/photo-1544164560-adac3045edb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjYXJwZW50ZXIlMjBhc3NlbWJsaW5nJTIwZnVybml0dXJlJTIwdG9vbHN8ZW58MHx8fHwxNzg5NDk3MDU2fDA&ixlib=rb-4.1.0&q=85",
};

export default function Home() {
  const { t, lang } = useLang();
  const settings = useSettings();
  const sh = t.seo.home;

  useEffect(() => {
    document.title = lang === "da"
      ? "IKEA møbelmontering i København & Nordsjælland | Belov Multiservice"
      : "IKEA furniture assembly in Copenhagen & North Zealand | Belov Multiservice";
  }, [lang]);

  const steps = [
    { icon: PackageCheck, title: t.how.step1Title, text: t.how.step1 },
    { icon: Calculator, title: t.how.step2Title, text: t.how.step2 },
    { icon: CalendarCheck, title: t.how.step3Title, text: t.how.step3 },
    { icon: Wrench, title: t.how.step4Title, text: t.how.step4 },
  ];

  const whys = [
    { icon: BadgeCheck, title: t.why.item1Title, text: t.why.item1 },
    { icon: Hammer, title: t.why.item2Title, text: t.why.item2 },
    { icon: ShieldCheck, title: t.why.item3Title, text: t.why.item3 },
    { icon: MapPinned, title: t.why.item4Title, text: t.why.item4 },
  ];

  const faqs = [1, 2, 3, 4, 5].map((n) => ({ q: t.faq[`q${n}`], a: t.faq[`a${n}`] }));

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#1E3A2F] bg-white px-3 py-1.5 rounded-full border border-stone-200">
                {t.hero.eyebrow}
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05] text-stone-900">
                {settings.hero_tagline?.[lang]}
              </h1>
              <p className="mt-5 text-lg text-stone-600 leading-relaxed max-w-xl">{t.hero.subtitle}</p>
              <ul className="mt-6 space-y-2">
                {[t.hero.point1, t.hero.point2, t.hero.point3].map((p, i) => (
                  <li key={i} className="flex items-center gap-2 text-stone-700">
                    <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" /> <span className="text-sm sm:text-base">{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/pax-montering" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1E3A2F] text-white px-7 py-4 text-base font-bold hover:bg-[#162D24] transition-colors" data-testid="hero-cta-btn">
                  {t.common.getPrice} <ArrowRight className="w-5 h-5" />
                </Link>
                <a href="#how" className="inline-flex items-center justify-center rounded-full border border-stone-300 bg-white px-7 py-4 text-base font-semibold text-stone-800 hover:bg-stone-50" data-testid="hero-secondary-btn">
                  {t.hero.ctaSecondary}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[4/5]">
                <img src={IMG.hero} alt={t.services.title} className="w-full h-full object-cover" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-emerald-700" /></div>
                  <div>
                    <div className="text-sm font-bold text-stone-900">{t.why.item1Title}</div>
                    <div className="text-xs text-stone-500">{t.common.vatBadge}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="max-w-2xl">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-stone-900">{t.how.title}</h2>
          <p className="mt-3 text-stone-600">{t.how.subtitle}</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div key={i} className="bg-white border border-stone-200 rounded-2xl p-6" data-testid={`how-step-${i + 1}`}>
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-xl bg-[#F4F1EA] flex items-center justify-center"><s.icon className="w-5 h-5 text-[#1E3A2F]" /></div>
                <span className="font-mono text-2xl font-bold text-stone-200">0{i + 1}</span>
              </div>
              <h3 className="mt-4 font-semibold text-stone-900">{s.title}</h3>
              <p className="mt-1 text-sm text-stone-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-stone-900">{t.services.title}</h2>
            <p className="mt-3 text-stone-600">{t.services.subtitle}</p>
          </div>
          <div className="mt-10 grid md:grid-cols-12 gap-6">
            <ServiceCard className="md:col-span-5" img={IMG.pax} icon={Boxes} badge={t.services.instant} badgeColor="emerald"
              title={t.services.paxTitle} desc={t.services.paxDesc} cta={t.services.paxCta} to="/pax-montering" testId="service-pax" />
            <ServiceCard className="md:col-span-4" img={IMG.kitchen} icon={ChefHat} badge={t.services.quote} badgeColor="amber"
              title={t.services.kitchenTitle} desc={t.services.kitchenDesc} cta={t.services.kitchenCta} to="/ikea-koekken" testId="service-kitchen" />
            <ServiceCard className="md:col-span-3" img={IMG.tools} icon={Sofa} badge={t.services.quote} badgeColor="amber"
              title={t.services.otherTitle} desc={t.services.otherDesc} cta={t.services.otherCta} to="/oevrige-moebler" testId="service-other" />
          </div>
        </div>
      </section>

      {/* SEO SERVICE OVERVIEW + LOCAL */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="home-seo">
        <div className="max-w-2xl">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-stone-900">{sh.blocksTitle}</h2>
        </div>
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {sh.blocks.map((b, i) => (
            <div key={i} className="bg-white border border-stone-200 rounded-2xl p-6 flex flex-col" data-testid={`home-seo-block-${i}`}>
              <h3 className="font-display text-lg font-bold text-stone-900">{b.title}</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed flex-1">{b.text}</p>
              <Link to={b.to} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A2F] hover:gap-2.5 transition-all">
                {b.link} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-12 bg-[#F4F1EA] rounded-3xl p-8 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-stone-900">{sh.localTitle}</h2>
          <p className="mt-3 text-stone-600 leading-relaxed max-w-2xl">{sh.localText}</p>
          <Link to="/serviceomraade" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A2F] hover:gap-2.5 transition-all" data-testid="home-seo-area-link">
            {sh.localLink} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* PAX INSTANT PRICE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="home-pax-calc">
        <div className="max-w-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-stone-900">{t.pax.heroTitle}</h2>
          <p className="mt-3 text-stone-600">{t.pax.heroSubtitle}</p>
        </div>
        <PaxCalculator />
      </section>

      {/* WHY */}
      <section className="bg-[#1E3A2F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">{t.why.title}</h2>
            <p className="mt-3 text-white/70">{t.why.subtitle}</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whys.map((w, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid={`why-${i + 1}`}>
                <w.icon className="w-7 h-7 text-amber-400" />
                <h3 className="mt-4 font-semibold">{w.title}</h3>
                <p className="mt-1 text-sm text-white/70 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-stone-900">{t.area.title}</h2>
            <p className="mt-3 text-stone-600">{t.area.subtitle}</p>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold" data-testid="home-area-city-links">
              <Link to="/moebelmontering-koebenhavn" className="text-[#1E3A2F] underline underline-offset-2" data-testid="home-link-koebenhavn">Møbelmontering i København</Link>
              <Link to="/moebelmontering-nordsjaelland" className="text-[#1E3A2F] underline underline-offset-2" data-testid="home-link-nordsjaelland">Møbelmontering i Nordsjælland</Link>
            </div>
            <div className="mt-6"><PostcodeChecker compact /></div>
          </div>
          <div className="rounded-3xl overflow-hidden aspect-[16/10] shadow-lg">
            <img src={IMG.tools} alt={t.area.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F4F1EA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-stone-900 text-center">{t.faq.title}</h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-stone-200">
                <AccordionTrigger className="text-left font-semibold text-stone-900 hover:no-underline" data-testid={`faq-q-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-stone-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="bg-[#1E3A2F] rounded-3xl px-6 py-14 sm:px-12 sm:py-20 text-center">
          <h2 className="text-2xl sm:text-4xl font-display font-extrabold text-white tracking-tight">{t.finalCta.title}</h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto">{t.finalCta.subtitle}</p>
          <Link to="/pax-montering" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#1E3A2F] px-8 py-4 text-base font-bold hover:bg-white/90 transition-colors" data-testid="final-cta-btn">
            {t.common.getPrice} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ className, img, icon: Icon, badge, badgeColor, title, desc, cta, to, testId }) {
  const badgeCls = badgeColor === "emerald"
    ? "bg-emerald-100 text-emerald-800"
    : "bg-amber-100 text-amber-800";
  return (
    <Link to={to} className={`group bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow flex flex-col ${className}`} data-testid={testId}>
      <div className="aspect-[16/10] overflow-hidden">
        <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#F4F1EA] flex items-center justify-center"><Icon className="w-5 h-5 text-[#1E3A2F]" /></div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badgeCls}`}>{badge}</span>
        </div>
        <h3 className="mt-4 font-display text-xl font-bold text-stone-900">{title}</h3>
        <p className="mt-1 text-sm text-stone-600 leading-relaxed flex-1">{desc}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A2F] group-hover:gap-2.5 transition-all">
          {cta} <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}
